import { createSelector } from 'reselect';

import { isActivityCompleted } from '../../lib/answers';
import READING_MODES from '../../data/reading_modes';
import { createActivityId } from '../../services/activities';
import { ACTIVITY_TYPES_ENUM, POST_READING } from '../../data/activity_types';
import { isChapterBook } from './bookInfo';

const getBookInfo = ({ bookInfo }) => bookInfo;

const getConfig = ({ config }) => config;

export const getReadingMode = state => (state.readingMode.allModesCompleted ? 'COMPLETE' : state.readingMode.currentMode);

export const getCurrentActivity = ({ inlineActivities: { currentActivityId } }) => currentActivityId;

export const getCurrentGraphicOrganizer = ({ graphicOrganizer: { currentQuestionId } }) => currentQuestionId;

const calculateProgress = (bookInfo, config = { hasPostReading: false, hasGraphicOrganizer: false }) => {
  const { columnsNumber, currentPage, layout, chapters } = bookInfo;
  const { hasPostReading, hasGraphicOrganizer } = config;
  const isSingleColumn = columnsNumber === 1;
  const { isFixedLayout, spreadCount } = layout;

  let progressSteps;
  let currentSpread;
  if (isFixedLayout) {
    // page count is the same as spread count, but we show two pages/spreads simultaneously
    // so progress should update on every two spreads - (0, 1), (2, 3) etc.
    progressSteps = Math.floor((spreadCount - 1) / 2);
    currentSpread = Math.floor(currentPage / 2);
  } else {
    // one spread contains as many pages as there are columns
    progressSteps = spreadCount - 1;
    currentSpread = isSingleColumn
      ? currentPage
      : Math.floor(currentPage / 2);
  }

  if (chapters.length > 0 && (hasPostReading || hasGraphicOrganizer)) {
    // eslint-disable-next-line no-unused-expressions
    progressSteps + 1;
  }

  if (progressSteps === 0) {
    // single spread view
    return 100;
  }
  return Math.ceil((currentSpread / progressSteps) * 100);
};

export const getProgress = createSelector([
  getBookInfo,
  getConfig,
], (bookInfo, config) => {
  const { pages: { canGoLeft }, chapters } = bookInfo;
  const numChapters = chapters && chapters.length;

  if (numChapters > 1 && !canGoLeft) {
    return 0;
  }
  return calculateProgress(bookInfo, config);
});

const getActivityPositions = ({ inlineActivities: { activityPositions } }) => activityPositions;

const getAnswers = ({ answers, inlineActivities }) => [
  ...inlineActivities.answeredActivities,
  ...answers.graphicOrganizer,
  ...answers.postReading,
];

const getActivities = ({ inlineActivities, graphicOrganizer, postReading }) => [
  ...Object.values(inlineActivities.all),
  ...graphicOrganizer.questions,
  ...postReading.questions.map(({ questionId }) => ({ activityId: questionId, activityType: POST_READING })),
];

const LAST_ACTIVITIES = [ACTIVITY_TYPES_ENUM.GRAPHIC_ORGANIZER_TYPE, POST_READING];

export const getUnfinishedActivities = createSelector(
  [getActivities, getAnswers, getActivityPositions],
  (activities, answers, positions) =>
    activities
      .reduce((acc, activity) => {
        const { activityId, activityType } = activity;
        const uniqueActivityId = createActivityId(activityId, activityType);
        const activityPosition = positions[uniqueActivityId] || {};
        const pageIndex = activityType === POST_READING ? 999 : activityPosition.pageIndex;

        if (typeof pageIndex !== 'number' || isActivityCompleted(answers, activity)) {
          return acc;
        }

        return acc.concat({
          activityId,
          activityType,
          pageIndex,
          order: LAST_ACTIVITIES.includes(activityType) ? 999 : activityPosition.activityOrder,
        });
      }, [])
      .sort(({ pageIndex: a, order: orderA }, { pageIndex: b, order: orderB }) => {
        if (a === b) {
          return orderA > orderB ? 1 : -1;
        }
        return a > b ? 1 : -1;
      }),
);

export const getActivitiesByPage = createSelector([getUnfinishedActivities], activities =>
  activities.reduce((acc, item) => {
    const { pageIndex } = item;

    return { ...acc, [pageIndex]: (acc[pageIndex] || []).concat(item) };
  }, {}),
);

export const isProgressBarInteractive = (eReaderState) => {
  const {
    bookInfo: { currentChapterId },
  } = eReaderState;

  const mode = getReadingMode(eReaderState);
  const isChapterTitlePage = !currentChapterId.includes('page');

  return isChapterBook(eReaderState) ? !isChapterTitlePage : Boolean(mode && mode !== READING_MODES.CLEAN);
};

export const hasMetCompletionThreshold = createSelector([getUnfinishedActivities], activities => !activities.length);

export const getActiveBadges = ({ inlineActivities, graphicOrganizer }) => {
  const { visibleActivities } = inlineActivities;
  const { activeQuestionsIds } = graphicOrganizer;

  const goQuestions = activeQuestionsIds.map(id => ({
    type: ACTIVITY_TYPES_ENUM.GRAPHIC_ORGANIZER_TYPE,
    activityId: id,
  }));
  const activitiesOnCurrentPage = visibleActivities.filter(
    ({ type }) => type !== ACTIVITY_TYPES_ENUM.GRAPHIC_ORGANIZER_TYPE,
  );

  return [...activitiesOnCurrentPage, ...goQuestions];
};
